/**
 * Phase 8 T-07 §8 E — Candidate detail page right rail in Q&A posture.
 *
 * Top half: empty-state illustration + "Get started" heading.
 * After first message: conversation thread (plain, no bubbles).
 * Bottom: sticky "Ask about this candidate…" input wired to the same
 * invokeLlamaChat + agentTools used by AgentPanel — no new backend wiring.
 */

import { useCallback, useRef, useState } from 'react';
import { Sparkles, User as UserIcon, Loader2, Send, StopCircle } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { invokeLlamaChat, type LlamaChatMessage } from '@core/ai/llamaChat';
import {
  AGENT_SYSTEM_PROMPT,
  TOOL_DEFINITIONS,
  OPENAI_TOOL_DEFINITIONS,
  parseOpenAIToolCalls,
  parseToolCall,
  executeTool,
  toolResultSummary,
  augmentAssistantHistoryForLlm,
} from '@core/ai/agentTools';

interface ThreadMsg {
  id: string;
  role: 'user' | 'agent';
  text: string;
  thinking?: boolean;
}

function MsgRow({ msg }: { msg: ThreadMsg }) {
  const isUser = msg.role === 'user';
  return (
    <div className="flex gap-2 py-2">
      <div className={[
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
        isUser
          ? 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200'
          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
      ].join(' ')}>
        {isUser ? <UserIcon className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary mb-0.5">
          {isUser ? 'You' : 'AI'}
        </p>
        {msg.thinking ? (
          <span className="flex items-center gap-1 text-[12px] italic vscode-text-tertiary">
            <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
          </span>
        ) : (
          <p className="whitespace-pre-wrap text-[13px] vscode-text-primary leading-relaxed">{msg.text}</p>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/20">
        <Sparkles className="h-6 w-6 text-emerald-500" />
      </div>
      <div>
        <p className="text-[14px] font-semibold vscode-text-primary">Get started by asking a question</p>
        <p className="mt-1 text-[12px] vscode-text-secondary leading-relaxed">
          Ask about the candidate, your notes,<br />or things you want to search for.
        </p>
      </div>
    </div>
  );
}

interface Props {
  candidateId: string;
  candidateName: string;
}

export function CandidateQARail({ candidateId, candidateName }: Props) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const [messages, setMessages] = useState<ThreadMsg[]>([]);
  const [history, setHistory] = useState<LlamaChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const systemPrompt = `${AGENT_SYSTEM_PROMPT}
When the thread contains [Agent context] under a previous reply, use it for follow-up questions instead of calling tools again.
The user is asking about candidate [Candidate context: ${candidateId}] (${candidateName}). Focus responses on this candidate.

${TOOL_DEFINITIONS}`;

  const run = useCallback(async (text: string) => {
    if (!text.trim() || streaming) return;
    abortRef.current = false;
    setStreaming(true);

    const userMsg: ThreadMsg = { id: crypto.randomUUID(), role: 'user', text: text.trim() };
    const thinkingMsg: ThreadMsg = { id: crypto.randomUUID(), role: 'agent', text: '', thinking: true };

    setMessages((m) => [...m, userMsg, thinkingMsg]);
    setDraft('');

    const newHistory: LlamaChatMessage[] = [
      ...history,
      { role: 'user', content: text.trim() },
    ];

    try {
      const resp = await invokeLlamaChat({
        orgId,
        messages: newHistory,
        systemPrompt,
        tools: OPENAI_TOOL_DEFINITIONS,
      });

      if (abortRef.current) return;

      const rawContent = resp.choices?.[0]?.message?.content ?? '';
      const toolCalls =
        parseOpenAIToolCalls(resp.choices?.[0]?.message) ??
        (rawContent ? [parseToolCall(rawContent)].filter(Boolean) : []);

      let finalText = rawContent;
      const assistantHistory = augmentAssistantHistoryForLlm(resp.choices?.[0]?.message ?? { role: 'assistant', content: rawContent });

      if (toolCalls && toolCalls.length > 0) {
        const results = await Promise.all(
          toolCalls.map((tc) => executeTool(tc!, orgId))
        );
        const summaries = results.map((r, i) => toolResultSummary(toolCalls![i]!, r)).join('\n\n');
        finalText = summaries || rawContent;

        const toolResultHistory: LlamaChatMessage[] = results.map((r, i) => ({
          role: 'tool',
          content: JSON.stringify(r),
          tool_call_id: (toolCalls![i] as Record<string, unknown>)?.id as string | undefined,
        }));

        setHistory([...newHistory, assistantHistory, ...toolResultHistory]);
      } else {
        setHistory([...newHistory, { role: 'assistant', content: rawContent }]);
      }

      setMessages((m) =>
        m.map((msg) =>
          msg.id === thinkingMsg.id
            ? { ...msg, text: finalText, thinking: false }
            : msg
        )
      );
    } catch (err) {
      if (abortRef.current) return;
      const errText = err instanceof Error ? err.message : 'Something went wrong.';
      setMessages((m) =>
        m.map((msg) =>
          msg.id === thinkingMsg.id
            ? { ...msg, text: errText, thinking: false }
            : msg
        )
      );
    } finally {
      if (!abortRef.current) setStreaming(false);
      window.requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [streaming, history, orgId, systemPrompt]);

  const stop = () => {
    abortRef.current = true;
    setStreaming(false);
    setMessages((m) => m.filter((msg) => !msg.thinking));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col border-l border-[var(--vscode-border-primary)]">
      {/* Header */}
      <div className="shrink-0 border-b border-[var(--vscode-border-primary)] px-4 py-2.5">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          <Sparkles className="h-3 w-3" /> AI Assistant
        </p>
      </div>

      {/* Thread */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="divide-y divide-[var(--vscode-border-primary)] px-4">
            {messages.map((msg) => (
              <MsgRow key={msg.id} msg={msg} />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-[var(--vscode-border-primary)] p-3">
        <div className="flex items-end gap-2 rounded-[10px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] px-2 py-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void run(draft);
              }
            }}
            placeholder={`Ask about ${candidateName}…`}
            rows={2}
            className="min-h-[36px] flex-1 resize-none bg-transparent text-[13px] vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none"
          />
          {streaming ? (
            <button
              type="button"
              onClick={stop}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
              title="Stop"
            >
              <StopCircle className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void run(draft)}
              disabled={!draft.trim()}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
              title="Send"
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
